import { z } from "zod";

type Brand<T, Name extends string> = T & { readonly __brand: Name };
export type ProcedureId = Brand<string, "ProcedureId">;
export type ProcedureSectionId = Brand<string, "ProcedureSectionId">;
export type ProcedureItemId = Brand<string, "ProcedureItemId">;

const idSchema = z.string().uuid();
function brandedId<T extends string>() {
  return idSchema.transform((value) => value as T);
}
export const procedureIdSchema = brandedId<ProcedureId>();
export const procedureSectionIdSchema = brandedId<ProcedureSectionId>();
export const procedureItemIdSchema = brandedId<ProcedureItemId>();

export const procedureStatusSchema = z.enum(["draft", "published", "unpublished", "deleted"]);
export const procedureItemTypeSchema = z.enum(["TEXT", "CHECKBOX", "NUMERIC", "SINGLE_CHOICE", "PHOTO"]);

const orderedSiblingSchema = z.object({ order: z.number().int().positive() });
const itemBaseSchema = orderedSiblingSchema.extend({
  id: procedureItemIdSchema,
  sectionId: procedureSectionIdSchema.nullable(),
  parentItemId: procedureItemIdSchema.nullable(),
  label: z.string().trim().min(1).max(10_000),
  description: z.string().trim().max(10_000).nullable(),
  required: z.boolean(),
});
const simpleItemSchema = itemBaseSchema.extend({
  type: z.enum(["TEXT", "CHECKBOX", "PHOTO"]),
  unit: z.never().optional(),
  options: z.never().optional(),
});
const numericItemSchema = itemBaseSchema.extend({
  type: z.literal("NUMERIC"),
  unit: z.string().trim().min(1).max(100).optional(),
  options: z.never().optional(),
});
const singleChoiceItemSchema = itemBaseSchema.extend({
  type: z.literal("SINGLE_CHOICE"),
  options: z.array(z.string().trim().min(1).max(255)).min(1).max(100).superRefine((options, context) => {
    if (new Set(options).size !== options.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Choice options must be unique." });
    }
  }),
  unit: z.never().optional(),
});
export const procedureItemSchema = z.union([simpleItemSchema, numericItemSchema, singleChoiceItemSchema]);

export const procedureSectionSchema = orderedSiblingSchema.extend({
  id: procedureSectionIdSchema,
  parentSectionId: procedureSectionIdSchema.nullable(),
  title: z.string().trim().min(1).max(255),
});

const lifecycleSchema = z.object({
  status: procedureStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
});

type SectionNode = { id: string; parentSectionId: string | null; order: number };
type ItemNode = { id: string; sectionId: string | null; parentItemId: string | null; order: number };

function validateTree(sections: SectionNode[], items: ItemNode[], context: z.RefinementCtx) {
  const sectionIds = new Set<string>();
  const itemIds = new Set<string>();
  for (const section of sections) {
    if (!sectionIds.add(section.id)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["sections"], message: "Section IDs must be unique." });
  }
  const sectionSiblings = new Map<string | null, SectionNode[]>();
  for (const section of sections) {
    if (section.parentSectionId !== null && !sectionIds.has(section.parentSectionId)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["sections"], message: "Section parents must reference an existing section." });
    const siblings = sectionSiblings.get(section.parentSectionId) ?? [];
    siblings.push(section);
    sectionSiblings.set(section.parentSectionId, siblings);
  }
  for (const siblings of sectionSiblings.values()) {
    if (new Set(siblings.map((section) => section.order)).size !== siblings.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ["sections"], message: "Section order must be unique among siblings." });
  }
  const itemSiblings = new Map<string, ItemNode[]>();
  for (const item of items) {
    if (!itemIds.add(item.id)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["items"], message: "Item IDs must be unique." });
    if (item.sectionId !== null && !sectionIds.has(item.sectionId)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["items"], message: "Items must reference an existing section." });
    const siblingKey = `${item.sectionId ?? "root"}:${item.parentItemId ?? "root"}`;
    const siblings = itemSiblings.get(siblingKey) ?? [];
    siblings.push(item);
    itemSiblings.set(siblingKey, siblings);
  }
  for (const item of items) {
    if (item.parentItemId !== null && !itemIds.has(item.parentItemId)) context.addIssue({ code: z.ZodIssueCode.custom, path: ["items"], message: "Item parents must reference an existing item." });
    const parent = item.parentItemId === null ? null : items.find((candidate) => candidate.id === item.parentItemId);
    if (parent !== null && parent !== undefined && parent.sectionId !== item.sectionId) context.addIssue({ code: z.ZodIssueCode.custom, path: ["items"], message: "Item parents must belong to the same section." });
  }
  for (const siblings of itemSiblings.values()) {
    if (new Set(siblings.map((item) => item.order)).size !== siblings.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ["items"], message: "Item order must be unique among siblings." });
  }
  for (const section of sections) {
    const seen = new Set<string>();
    let current: string | null = section.id;
    for (let depth = 0; current !== null && depth <= sections.length; depth += 1) {
      if (seen.has(current)) { context.addIssue({ code: z.ZodIssueCode.custom, path: ["sections"], message: "Section hierarchy cannot contain cycles." }); break; }
      seen.add(current);
      current = sections.find((candidate) => candidate.id === current)?.parentSectionId ?? null;
    }
  }
  for (const item of items) {
    const seen = new Set<string>();
    let current: string | null = item.id;
    for (let depth = 0; current !== null && depth <= items.length; depth += 1) {
      if (seen.has(current)) { context.addIssue({ code: z.ZodIssueCode.custom, path: ["items"], message: "Item hierarchy cannot contain cycles." }); break; }
      seen.add(current);
      current = items.find((candidate) => candidate.id === current)?.parentItemId ?? null;
    }
  }
}

export const procedureSchema = z.object({
  id: procedureIdSchema,
  title: z.string().trim().min(1).max(255),
  description: z.string().trim().max(10_000).nullable(),
  owner: z.string().trim().min(1).max(255),
  category: z
    .string()
    .trim()
    .max(255)
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  categories: z.array(z.string().trim().min(1).max(255)).max(50).default([]),
  categoryIds: z.array(z.number().int().positive()).max(50).default([]),
  team: z.string().trim().min(1).max(255).nullable(),
  version: z.number().int().positive(),
  sections: z.array(procedureSectionSchema).max(500),
  items: z.array(procedureItemSchema).max(2_000),
  ...lifecycleSchema.shape,
}).superRefine((procedure, context) => {
  if (procedure.status === "published" && procedure.publishedAt === null) context.addIssue({ code: z.ZodIssueCode.custom, path: ["publishedAt"], message: "Published Procedures require publishedAt." });
  if (procedure.status !== "published" && procedure.publishedAt !== null) context.addIssue({ code: z.ZodIssueCode.custom, path: ["publishedAt"], message: "Only published Procedures may have publishedAt." });
  validateTree(procedure.sections, procedure.items, context);
});

export type ProcedureItem = z.infer<typeof procedureItemSchema>;
export type ProcedureSection = z.infer<typeof procedureSectionSchema>;
export type Procedure = z.infer<typeof procedureSchema>;
export type ProcedureItemType = z.infer<typeof procedureItemTypeSchema>;
export type ProcedureStatus = z.infer<typeof procedureStatusSchema>;
export * from "./display-order";
